// React imports and libraries
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import api from '../config/api';

// API calls / Slices imports
import { createOrder, fetchOrders } from '../slices/ordersSlice';
import { fetchUserSetting, fetchUsers } from '../slices/userSlice';
import { usePricing } from '../hooks/usePricing';
import { getCategoryId } from '../utils/PricingEngine';
import CreateNewClient from './orderSections/CreateNewClient';
import {
	fetchClientsOrder,
	resetClientData,
} from '../slices/clientSlice';

// Components imports
import StyleSettings from './orderSections/styleSettings';
import PricePreview from './orderSections/PricePreview';

// Refactored Sections
import OrderPriceSummary from './NewOrderSections/OrderPriceSummary';
import OrderClientInfo from './NewOrderSections/OrderClientInfo';
import OrderAddressSection from './NewOrderSections/OrderAddressSection';
import OrderInstallerSection from './NewOrderSections/OrderInstallerSection';
import OrderStyleSection from './NewOrderSections/OrderStyleSection';
import OrderDimensionsSection from './NewOrderSections/OrderDimensionsSection';
import OrderSidesEndsSection from './NewOrderSections/OrderSidesEndsSection';
import OrderAdditionalOptionsSection from './NewOrderSections/OrderAdditionalOptionsSection';
import OrderDoorsWindowsSection from './NewOrderSections/OrderDoorsWindowsSection';
import OrderColorsSection from './NewOrderSections/OrderColorsSection';
import OrderWarrantySection from './NewOrderSections/OrderWarrantySection';
import OrderDiscountsSection from './NewOrderSections/OrderDiscountsSection';
import OrderEngineeringSection from './NewOrderSections/OrderEngineeringSection';
import { calculateEngineeringQuote } from '../pricing/engineeringQuote';

// Utility functions, data and styles
import { formatPhoneNumber } from '../hooks/phoneFormat';
import { normalizeClientAddress } from '../utils/addressUtils';
import {
	gableEndsOptions,
	sidewallOptions,
	windBracingOptions,
	jTrimOptions,
	sideWallSidingPanelsData,
	additionalbracing,
	AnchorsOptions,
	panelUpgradeOptions,
	leanRoofPitch,
} from '../formData/aditionalOptionsData';
import {
	styleOptions,
	centerStorageOptions,
	roofStyleList,
	hexBarnRoofStyle,
	installationSurfaceOptions,
	roofPitchOptions,
} from '../formData/styleData';

// Icons
import { ChevronDown, ChevronRight, Download } from 'react-feather';
import Modal from 'react-modal';

Modal.setAppElement('#app');

const customStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    content: {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        border: 'none',
        background: 'none',
        padding: 0,
        overflow: 'visible',
    },
};


export default function NewOrder({ onClose }) {
	// ---------------------------------------------------------------------------
	// Redux & base config
	// ---------------------------------------------------------------------------
	const dispatch = useDispatch();
	const token = localStorage.getItem('token');
	const apiKey = process.env.REACT_APP_API_TAXES_KEY;

	// Global state selectors
	const client = useSelector((state) => state?.clients?.clientById);
	const usersList = useSelector((state) => state.auth?.users);
	const user = useSelector((state) => state?.auth?.self?.id);
	const users = useSelector((state) => state?.auth?.users);
	const userCommission = useSelector(
		(state) => state?.auth?.userSetting?.metadata?.commission
	);

	// Constant configuration
	const installationFee = 0;
	const pageSize = 2000;

	// Filter installers from user list
	const installers = users?.filter((user) => user.role === 'INSTALLER') || [];
	const clientAddress = useMemo(() => normalizeClientAddress(client), [client]);

	// ---------------------------------------------------------------------------
	// Local state: Client, Tax & UI
	// ---------------------------------------------------------------------------
	const [taxRate, setTaxRate] = useState(null);
	const [loadingTax, setLoadingTax] = useState(false);
	const [taxError, setTaxError] = useState(null);

	const [totalDiscount, setTotalDiscount] = useState(0);

	// Session protection: prevent auto-logout while building a quote
	// Also reset client data on mount to ensure user selects a client for a new quote
	useEffect(() => {
		localStorage.setItem('quoteInProgress', 'true');
		
		// Reset client state on mount
		localStorage.removeItem('clientId');
		dispatch(resetClientData());

		return () => {
			localStorage.removeItem('quoteInProgress');
		};
	}, [dispatch]);

	// Selections & Configuration
	const [sideWallSidingPanels, setSideWallSidingPanels] = useState([]);
	const [sidewallCosts, setSidewallCosts] = useState({});
	const [doorWindowSelections, setDoorWindowSelections] = useState([]);
	const [formAdditionalOptionsData, setFormAdditionalOptionsData] = useState({
        sidewall: 'horizontal',
        gableEndSidingPanels: 'horizontal',
        frameGauge: '1',
        aditionalBracing: 'none',
        anchors: 'none',
        panelUpgrade: 'standardPanel29Gauge',
    });

	const [sections, setSections] = useState({
		client: false, address: false, orderForm: false,
		styleTab: false, sizeTab: false, sidesTab: false,
		additionalsTab: false, doorsTab: false, colorsTab: false,
		warrantyTab: false, notesTab: false, engineeringTab: false,
	});

	const [engineeringInput, setEngineeringInput] = useState({
		enabled: false,
		buildingType: '',
		wantsCalculations: false,
		isRush: false,
		isPriority: false,
		isResidential: false,
		isLoafingShed: false,
	});

	// react-hook-form
	const { register, control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
		defaultValues: {
			installationSurface: 'concrete', roofStyle: 'standard',
			address: '', city: '', state: '', postalCode: '',
			items: [{ materialId: '389', description: '14 Gauge Base Only', quantity: '1' }],
		},
	});
	const watchedPostalCode = useWatch({ control, name: 'postalCode' });

    // Update form default values when client changes
    useEffect(() => {
        if (client) {
            reset({
                installationSurface: 'concrete', 
                roofStyle: 'standard',
                address: clientAddress.address,
                city: clientAddress.city,
                state: clientAddress.state,
                postalCode: clientAddress.postalCode,
                items: [{ materialId: '389', description: '14 Gauge Base Only', quantity: '1' }],
            });
        }
    }, [client, clientAddress, reset]);

	// ---------------------------------------------------------------------------
	// Building Dimensions & Styles
	// ---------------------------------------------------------------------------
	const [colors, setColors] = useState({
		roofColor: 'white', intRoofColor: 'white', trimColor: 'white',
		endWallsColor: 'white', gableEndColor: 'white', sideWallColor: 'white', wainscotColor: 'white',
	});

	const [selectedStyle, setSelectestyle] = useState('carport');
	const [selectedRoofStyle, setSelectedRoofStyle] = useState('');
	const [selectedRoofPitch, setSelectedRoofPitch] = useState('');

	// Center
	const [cSWidth, setCSWidth] = useState('20');
	const [cSLength, setCSLength] = useState('30');
	const [cSHeight, setCSHeight] = useState('7');
	const [dephCentralStorage, setDephCentralStorage] = useState('10');

	// Lean-tos
	const [lLWidth, setLLWidth] = useState('');
	const [lLLength, setLLLength] = useState('');
	const [lLHeight, setLLHeight] = useState('');
	const [dephLeftStorage, setDephLeftStorage] = useState('10');

	const [rLWidth, setRLWidth] = useState('');
	const [rLLength, setRLLength] = useState('');
	const [rLHeight, setRLHeight] = useState('');
	const [dephRightStorage, setDephRightStorage] = useState('10');

	const [fLWidth, setFLWidth] = useState('');
	const [fLLength, setFLLength] = useState('');
	const [fLHeight, setFLHeight] = useState('');
	const [dephFrontStorage, setDephFrontStorage] = useState('10');

	const [bLWidth, setBLWidth] = useState('');
	const [bLLength, setBLLength] = useState('');
	const [bLHeight, setBLHeight] = useState('');
	const [dephBackStorage, setDephBackStorage] = useState('10');

	const [leftLean, setLeftLean] = useState('none');
	const [rightLean, setRightLean] = useState('none');
	const [frontLean, setFrontLean] = useState('none');
	const [backLean, setBackLean] = useState('none');

	const [centerStorage, setCenterStorage] = useState('none');
	const [storageLocation, setStorageLocation] = useState('none');
	const [totalSideWallPrice, setTotalSideWallPrice] = useState(0);
	const [sideEndData, setSideEndData] = useState({});
	const [filterCategoryId, setFilterCategoryId] = useState('');

	// ---------------------------------------------------------------------------
	// Pricing Hook
	// ---------------------------------------------------------------------------
	const { prices, getDynamicPrice } = usePricing({
		dispatch, token,
		selectedStyle,
		cSWidth, cSLength, cSHeight,
		filterCategoryId, selectedRoofStyle, selectedRoofPitch,
		leftLean, lLWidth, lLLength, lLHeight,
		rightLean, rLWidth, rLLength, rLHeight,
		frontLean, fLWidth, fLLength, fLHeight,
		backLean, bLWidth, bLLength, bLHeight,
		sideEndData, totalSideWallPrice,
		doorWindowSelections,
		totalDiscount: watch('totalDiscount') || 0,
		sidewallCosts,
		centerStorage,
		storageLocation, dephCentralStorage, dephLeftStorage, dephRightStorage, dephFrontStorage, dephBackStorage,
		formAdditionalOptionsData,
	});

	const {
		totalCenterGauge, totalCenterLegPrice, leftLeanPrice, totalLeftLegPrice,
		rightLeanPrice, totalRightLegPrice, frontLeanPrice, totalFrontLegPrice,
		backLeanPrice, totalBackLegPrice, totalRoofStylePrice, totalRoofPitchPrice,
		leftSideRoofPitchPrice, rightSideRoofPitchPrice, frontRoofPitchPrice, backRoofPitchPrice,
		subTotal, totalEstimate, totalSidewallCost, windosandDoorPrice,
		centerStoragePrice, leftleanstoragePrice, rightStoragePrice, frontStoragePrice, backStoragePrice,
		totalInsulationPrice, panelUpgradePrice, anchorsTotalPrice, bracingTotalPrice, autoBracingTotalPrice, autoBracingCount, trussesTotalPrice, texasCutTotal, storagePanelsTotal, storagePanelsBreakdown,
		foamClosureTotal,
	} = prices;
    

	// A&A Engineering quote — recalculates whenever inputs or dimensions change
	const engineeringQuote = useMemo(() => {
		if (!engineeringInput.enabled || !engineeringInput.buildingType) return null;
		return calculateEngineeringQuote({
			...engineeringInput,
			widthFt: parseFloat(cSWidth) || 0,
			lengthFt: parseFloat(cSLength) || 0,
			locationState: clientAddress?.state || '',
			locationCityCounty: clientAddress?.city || '',
		});
	}, [engineeringInput, cSWidth, cSLength, clientAddress]);

	const engineeringPlansFee = engineeringQuote?.total ?? 0;

	// ---------------------------------------------------------------------------
	// Effects: bootstrap data (user, users, settings, client, tax)
	// ---------------------------------------------------------------------------

	// Load user list once on mount
	useEffect(() => {
		dispatch(fetchUsers({ pageSize: 2000 }));
	}, [dispatch]);

	// Load user settings (commission, etc.) once user id is available
	useEffect(() => {
		if (user) {
			// 'user' is state.auth.self.id
			dispatch(fetchUserSetting(user));
		}
	}, [dispatch, user]);

	// When client changes, hydrate the form fields

	useEffect(() => {
		if (client) {
			reset((prev) => ({
				...prev,
				address: clientAddress.address,
				city: clientAddress.city,
				state: clientAddress.state,
				postalCode: clientAddress.postalCode,
			}));
		}
	}, [client, clientAddress, reset]);

	// Fetch tax rate based on client postalCode (uses external API)
	useEffect(() => {
		const postalCode = (watchedPostalCode || '').trim();

		setTaxRate(null);
		setTaxError(null);

		if (postalCode.length !== 5 || !apiKey) {
			setLoadingTax(false);
			return undefined;
		}

		let isCancelled = false;
		setLoadingTax(true);

		fetch(`https://api.api-ninjas.com/v1/salestax?zip_code=${postalCode}`, {
			method: 'GET',
			headers: {
				'X-Api-Key': apiKey,
			},
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error('Failed to fetch tax rate');
				}
				return response.json();
			})
			.then((data) => {
				if (isCancelled) {
					return;
				}

				if (data && data[0] && data[0].state_rate) {
					setTaxRate({ state_rate: data[0].state_rate });
				} else {
					setTaxError(true);
				}
			})
			.catch((error) => {
				if (!isCancelled) {
					console.error('Error fetching tax rate:', error);
					setTaxError(true);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setLoadingTax(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [watchedPostalCode, apiKey]);

	const styleColorDefaults = {
		carport:      { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'pewtergray', endWallsColor: 'pewtergray', gableEndColor: 'pewtergray' },
		tw:           { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'pewtergray', endWallsColor: 'pewtergray', gableEndColor: 'pewtergray' },
		leanto:       { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'clay',       endWallsColor: 'clay',       gableEndColor: 'clay'       },
		garage:       { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'pewtergray', endWallsColor: 'pewtergray', gableEndColor: 'pewtergray' },
		garageleanto: { roofColor: 'slateblue',  trimColor: 'white', sideWallColor: 'slateblue',  endWallsColor: 'slateblue',  gableEndColor: 'slateblue'  },
		utility:      { roofColor: 'quakergray', trimColor: 'white', sideWallColor: 'quakergray', endWallsColor: 'quakergray', gableEndColor: 'quakergray' },
		rv:           { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'pewtergray', endWallsColor: 'pewtergray', gableEndColor: 'pewtergray' },
		barn:         { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'pewtergray', endWallsColor: 'pewtergray', gableEndColor: 'pewtergray' },
		openbarn:     { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'pewtergray', endWallsColor: 'pewtergray', gableEndColor: 'pewtergray' },
		doubleleanto: { roofColor: 'clay',       trimColor: 'white', sideWallColor: 'pewtergray', endWallsColor: 'pewtergray', gableEndColor: 'pewtergray' },
	};

	// Default roof style / pitch / colors / leans when style changes
	useEffect(() => {
		if (selectedStyle) {
			const verticalStyles = ['garage', 'tw', 'barn', 'slbarn', 'utility', 'rv', 'leanto', 'garageleanto', 'openbarn', 'doubleleanto'];
			setSelectedRoofStyle(verticalStyles.includes(selectedStyle) ? 'vertical' : 'standard');
			setSelectedRoofPitch(selectedStyle === 'leanto' ? '2/12' : '3/12');
			setCenterStorage(selectedStyle === 'utility' ? 'back' : 'none');
			const colorDefaults = styleColorDefaults[selectedStyle];
			if (colorDefaults) {
				setColors((prev) => ({ ...prev, ...colorDefaults, intRoofColor: prev.intRoofColor, wainscotColor: 'none' }));
			}
			if (selectedStyle === 'barn' || selectedStyle === 'slbarn') {
				setLeftLean('leanToOnly');
				setRightLean('leanToOnly');
				setFrontLean('none');
				setBackLean('none');
			} else {
				setLeftLean('none');
				setRightLean('none');
				setFrontLean('none');
				setBackLean('none');
			}
		}
	}, [selectedStyle]);



	// ---------------------------------------------------------------------------
	// Handlers: form, UI toggles, style selection
	// ---------------------------------------------------------------------------

	// Callback from SideEnds: saves the full side/ends configuration
	const handleDataChange = useCallback((collectedData) => {
		setSideEndData(collectedData);
		console.log('Data received from SideEnds:', collectedData);
	}, []);

	// Toggle accordion section visibility
	const toggleSection = (section, e) => {
		e.preventDefault(); // Prevent default form submit behavior on header click
		setSections((prevSections) => ({
			...prevSections,
			[section]: !prevSections[section],
		}));
	};

	// Helper: resolve user name from userId (for notes)
	const getUserName = (userId) => {
		if (!userId) {
			return 'Unknown';
		}
		const user = usersList?.find(
			(user) => user?.id?.trim().toLowerCase() === userId.trim().toLowerCase()
		);
		return user ? user.firstName : 'Unknown';
	};

	// Reset client selection and clear client data
	const handdelDifferentClient = useCallback(() => {
		localStorage.removeItem('clientId');
		dispatch(resetClientData());
	}, [dispatch]);

	// Propagate color changes from Colors component
	const handleColorChange = useCallback((newColors) => {
		setColors(newColors);
	}, []);

	// Building style selection handler with confirmation
	const [showResetModal, setShowResetModal] = useState(false);
	const [pendingStyle, setPendingStyle] = useState(null);

	const handleOnChange = useCallback((e) => {
		const newStyle = e.target.value;
		if (newStyle === selectedStyle) return;

		setPendingStyle(newStyle);
		setShowResetModal(true);
	}, [selectedStyle]);

	const confirmReset = useCallback(() => {
		if (pendingStyle) {
			setSelectestyle(pendingStyle);
		}
		setShowResetModal(false);
		setPendingStyle(null);
	}, [pendingStyle]);

	const cancelReset = useCallback(() => {
		setShowResetModal(false);
		setPendingStyle(null);
	}, []);

	// Center storage location handler
	const handleCenterStorageChange = useCallback((e) => {
		setCenterStorage(e.target.value);
	}, []);

	// Roof pitch selection handler
	const handleRoofPitchChange = useCallback((event) => {
		setSelectedRoofPitch(event.target.value);
	}, []);

	// Roof style selection handler
	const handleRoofStyleChange = useCallback((event) => {
		setSelectedRoofStyle(event.target.value);
	}, []);

	const handleFilterCategoryId = useCallback((event) => {
		const val = event.target.value;
		setFilterCategoryId(val);
	}, []);

	const handleFormDataChange = useCallback((data) => {
		setFormAdditionalOptionsData(data);
	}, []);

	// ---------------------------------------------------------------------------
	// Category selection (gauge / style / lean-to)
	// ---------------------------------------------------------------------------

	useEffect(() => {
		const gauge = formAdditionalOptionsData.frameGauge || 1;
		const hasLeanTo = (leftLean && leftLean !== 'none') || (rightLean && rightLean !== 'none');
		setFilterCategoryId(getCategoryId(gauge, selectedStyle, hasLeanTo));
	}, [selectedStyle, leftLean, rightLean, formAdditionalOptionsData.frameGauge]);

	// ---------------------------------------------------------------------------
	// Walls & gable-end total value
	// ---------------------------------------------------------------------------

	// Callback for a simple numeric walls total (used in subtotal)
	const handleValuesWallsChange = useCallback((values) => {
		setTotalSideWallPrice(values);
	}, []);

	// ---------------------------------------------------------------------------
	// Legacy “calculateAndApplyFilters” aggregator (kept for future expansion)
	// ---------------------------------------------------------------------------


	// ---------------------------------------------------------------------------
	// Form submit: build order payload and create order
	// ---------------------------------------------------------------------------

	const onSubmit = async (data) => {
		// Helper to map CRM items to 3D portal format
		const mapTo3DItem = (item) => {
            const categoryLabels = {
                door: 'Walk-in Door',
                window: 'Window',
                frameouts: 'Frameout',
                garageDoor: 'Garage Door',
                rollupDoor: 'Roll-up Door',
                skylight: 'Skylight',
                vents: 'Vent',
            };
            
            const label = categoryLabels[item.category] || item.description || 'Item';

			return {
				type: item.description, // Mapped type/description
				name: item.description, // specific name/SKU
                categoryLabel: label, // Human readable label
				sizeH: 0, // Default placeholders
				sizeW: 0,
				itemRef: null, // null for CRM
				position: {
					x: parseFloat(item.metadata?.locationLeft) || 0,
					y: parseFloat(item.metadata?.locationBottom) || 0,
				},
                price: parseFloat(item.defaultPrice) || 0,
                quantity: parseInt(item.quantity, 10) || 1,
                totalPrice: (parseFloat(item.defaultPrice) || 0) * (parseInt(item.quantity, 10) || 1),
                // Removed wall/wallSide as it's defined by the group key
			};
		};

        // Helper to group items by side for 3D portal structure
        const groupItemsBySide = (sectionItems) => {
            const grouped = {
                front: [],
                back: [],
                left: [],
                right: [],
                roof: [],
                storage: []
            };

            sectionItems.forEach(item => {
                const side = item.metadata?.buildingSides?.toLowerCase();
                const mappedItem = mapTo3DItem(item);

                if (side && side.includes('front')) grouped.front.push(mappedItem);
                else if (side && side.includes('back')) grouped.back.push(mappedItem);
                else if (side && side.includes('left')) grouped.left.push(mappedItem);
                else if (side && side.includes('right')) grouped.right.push(mappedItem);
                else if (side && side.includes('roof')) grouped.roof.push(mappedItem);
                else grouped.front.push(mappedItem); // Default to front if unknown
            });
            return grouped;
        };

        // Helper to format color object
        const formatColor = (name) => ({ name: name || 'None', color: '#FFFFFF' }); // Default hex as we don't have map



		// commission calculation (legacy check updated to value check)
		const installerCommission = selectedRoofStyle === 'vertical';

		// Flatten doors/windows selections into payload-ready items JSON string
        // This is kept for backward compatibility if backend still expects 'centerSectionItems'
        // But we are also sending full structure in other metadata fields.
		const centerSectionItemsLegacy = JSON.stringify(
			doorWindowSelections.map((selection) => ({
				description: selection.description,
				materialId: parseInt(selection.materialId, 10) || 'Unknown',
				quantity: parseInt(selection.quantity, 10),
				unitPrice: parseFloat(selection.defaultPrice) || 0,
				metadata: {
					locationLeft: selection.metadata?.locationLeft,
                    locationBottom: selection.metadata?.locationBottom,
					section: selection.metadata?.section || 'Unknown',
					buildingSides: selection.metadata?.buildingSides,
					defaultPrice: parseFloat(selection.defaultPrice) || 0,
				},
			}))
		);

        // --- Build 3D Portal Compatible Metadata Structures ---

        // Center Section
        const centerSectionData = {
            id: 'centerSection',
            roofStyle: selectedRoofStyle,
            size: { width: cSWidth, length: cSLength, height: cSHeight },
            sidesAndEnds: sideEndData?.configuration?.centerSection || {}, // Assumption: sideEndData structure
            // Items filtered for center section
            items: doorWindowSelections
                .filter(item => item.metadata?.section === 'centerSection')
                .map(mapTo3DItem),
            windBracing: data.windBracing,
            jTrim: data.jTrim,
            mbPrice: totalEstimate,
            roofPitchOptions: data.roofPitchOptions,
        };

        // Left Lean-To
        const leftLeanToData = {
            id: 'leftLeanTo',
            leanTo: leftLean !== 'none',
            size: { width: lLWidth, length: lLLength, height: lLHeight },
            sidesAndEnds: sideEndData?.configuration?.leftLeanToSide || {},
            items: doorWindowSelections
                .filter(item => item.metadata?.section === 'leftLeanTo')
                .map(mapTo3DItem),
            windBracing: data.windBracing, // global for now
            jTrim: data.jTrim,
            roofPitchOptions: data.roofPitchOptions,
            llPrice: leftLeanPrice,
            lLegPrice: totalLeftLegPrice,
        };

        // Right Lean-To
        const rightLeanToData = {
            id: 'rightLeanTo',
            leanTo: rightLean !== 'none',
            size: { width: rLWidth, length: rLLength, height: rLHeight },
            sidesAndEnds: sideEndData?.configuration?.rightLeanToSide || {},
            items: doorWindowSelections
                .filter(item => item.metadata?.section === 'rightLeanTo')
                .map(mapTo3DItem),
            windBracing: data.windBracing,
            jTrim: data.jTrim,
            roofPitchOptions: data.roofPitchOptions,
            rlPrice: rightLeanPrice,
            rLegPrice: totalRightLegPrice,
        };

        // Front Lean-To
        const frontLeanToData = {
            id: 'frontLeanTo',
            leanTo: frontLean !== 'none',
            size: { width: fLWidth, length: fLLength, height: fLHeight },
            sidesAndEnds: sideEndData?.configuration?.frontLeanToSide || {},
            items: doorWindowSelections
                .filter(item => item.metadata?.section === 'frontLeanTo')
                .map(mapTo3DItem),
            windBracing: data.windBracing,
            jTrim: data.jTrim,
            roofPitchOptions: data.roofPitchOptions,
            flPrice: frontLeanPrice,
            fLegPrice: totalFrontLegPrice,
        };

        // Back Lean-To
        const backLeanToData = {
            id: 'backLeanTo',
            leanTo: backLean !== 'none',
            size: { width: bLWidth, length: bLLength, height: bLHeight },
            sidesAndEnds: sideEndData?.configuration?.backLeanToSide || {},
            items: doorWindowSelections
                .filter(item => item.metadata?.section === 'backLeanTo')
                .map(mapTo3DItem),
            windBracing: data.windBracing,
            jTrim: data.jTrim,
            roofPitchOptions: data.roofPitchOptions,
            blPrice: backLeanPrice,
            bLegPrice: totalBackLegPrice,
        };

        // Top-level ITEMS array for Billing/Backend
        // Mapping doorWindowSelections to the backend Item structure
        const billingItems = doorWindowSelections.map(selection => ({
            description: selection.description,
            materialId: parseInt(selection.materialId, 10) || 0, // Ensure valid ID or 0 if missing
            quantity: parseInt(selection.quantity, 10) || 1,
            unitPrice: parseFloat(selection.defaultPrice) || 0,
            metadata: {
                section: selection.metadata?.section ? String(selection.metadata.section) : 'Unknown',
                side: selection.metadata?.buildingSides ? String(selection.metadata.buildingSides) : '',
                locationLeft: selection.metadata?.locationLeft ? String(selection.metadata.locationLeft) : '',
                locationBottom: selection.metadata?.locationBottom ? String(selection.metadata.locationBottom) : '',
                categoryId: selection.categoryId ? String(selection.categoryId) : ''
            }
        }));

// Core order payload

		// Calculate Totals
		const discountAmount = parseFloat(data?.totalDiscount) || 0.0;
		const calculatedSubTotal = totalEstimate - discountAmount;
		
		const installationPercentage = 0;
		const installationAmount = 0;

		const taxRateValue = taxRate?.state_rate || 0.0725; // Default 7.25%
		const taxAmount = calculatedSubTotal * taxRateValue;

		const total = calculatedSubTotal + taxAmount + engineeringPlansFee;

		// Dynamic Order Payload
        console.log('Client Object:', client); // Debug logging

		const orderPayload = {
			// --- 3D spec fields (in spec order) ---
			clientId: client.id,
			salesmanId: String(user).toLowerCase(),
			isVerticalRoof: selectedRoofStyle === 'vertical',
			reference: 0,
			address: data?.address || clientAddress.address,
			city: data?.city || clientAddress.city,
			state: data?.state || clientAddress.state,
			postalCode: data?.postalCode || clientAddress.postalCode,
			source: 'CRM',
			items: [],
			commission: userCommission || 0,
			equipmentAmount: parseFloat(0.0) || 0.0,
			drawingsAmount: engineeringPlansFee,
			discountAmount: discountAmount,
			transportationFee: parseFloat(0.0) || 0.0,
			total: total,

			// --- CRM-only top-level extras ---
			installerId: data?.installerId || null,
			installationAmount: installationAmount,
			installationPercentage: installationPercentage,
			taxAmount: taxAmount,
			taxPercentage: taxRateValue,

			metadata: {
				// --- 3D spec metadata fields (in spec order) ---
				model: selectedStyle,
				roofPith: selectedRoofPitch,
				roofStyle: selectedRoofStyle,

				colors: JSON.stringify({
					// spec keys first
					roof: formatColor(data.roofColor),
					trim: formatColor(data.trimColor),
					sideWalls: formatColor(data.wallColor),
					endWalls: formatColor(data.endWallColor),
					// CRM extras
					intRoof: formatColor(data.intRoofColor),
					sideWall: formatColor(data.wallColor),
					gableEnd: formatColor(data.endWallColor),
					wainscotColor: formatColor(data.wainscotColor),
					insulationPrice: totalInsulationPrice,
					insulationDetails: JSON.stringify(formAdditionalOptionsData),
				}),

				price: JSON.stringify({
					subTotalPrice: calculatedSubTotal,
					totalPrice: total,
					taxAmount: taxAmount,
					taxPercentage: taxRateValue * 100,
					installationAmount: installationAmount,
					installationPercentage: installationPercentage * 100,
					// CRM extra
					engineeringPlansFee: engineeringPlansFee,
				}),

				installationSurface: data.installationSurface || "concrete",
				centerStorageOptions: centerStorage,

				centerSection: JSON.stringify({
					size: {
						width: parseFloat(cSWidth) || 0,
						length: parseFloat(cSLength) || 0,
						height: parseFloat(cSHeight) || 0,
					},
					sidesAndEnds: sideEndData?.configuration?.centerSection || {},
					items: groupItemsBySide(doorWindowSelections.filter(item => item.metadata?.section === 'centerSection')),
					storage: {
						depth: (centerStorage === 'none' || !centerStorage) ? 0 : (parseFloat(dephCentralStorage) || 0),
						side: (centerStorage === 'none' || !centerStorage) ? null : centerStorage,
					},
					price: prices.centerPriceArray || [],
					// CRM extras
					id: 'centerSection',
					roofStyle: selectedRoofStyle,
					windBracing: data.windBracing,
					jTrim: data.jTrim,
					mbPrice: parseFloat(totalEstimate) || 0,
					roofPitchOptions: data.roofPitchOptions,
				}),

				leftLeanTo: JSON.stringify({
					leanTo: leftLean !== 'none',
					size: {
						width: parseFloat(lLWidth) || 0,
						length: parseFloat(lLLength) || 0,
						height: parseFloat(lLHeight) || 0,
					},
					sidesAndEnds: sideEndData?.configuration?.leftLeanToSide || {},
					items: (() => {
						const sectionItems = doorWindowSelections.filter(item => item.metadata?.section === 'leftLeanTo');
						return sectionItems.length > 0 ? groupItemsBySide(sectionItems) : {};
					})(),
					storage: {
						depth: parseFloat(dephLeftStorage) || 0,
						side: leftLean !== 'none' ? 'left' : null,
					},
					price: prices.leftPriceArray || [],
					// CRM extras
					id: 'leftLeanTo',
					windBracing: data.windBracing,
					jTrim: data.jTrim,
					roofPitchOptions: data.roofPitchOptions,
					llPrice: parseFloat(leftLeanPrice) || 0,
					lLegPrice: parseFloat(totalLeftLegPrice) || 0,
				}),

				rightLeanTo: JSON.stringify({
					leanTo: rightLean !== 'none',
					size: {
						width: parseFloat(rLWidth) || 0,
						length: parseFloat(rLLength) || 0,
						height: parseFloat(rLHeight) || 0,
					},
					sidesAndEnds: sideEndData?.configuration?.rightLeanToSide || {},
					items: (() => {
						const sectionItems = doorWindowSelections.filter(item => item.metadata?.section === 'rightLeanTo');
						return sectionItems.length > 0 ? groupItemsBySide(sectionItems) : {};
					})(),
					storage: {
						depth: parseFloat(dephRightStorage) || 0,
						side: rightLean !== 'none' ? 'right' : null,
					},
					price: prices.rightPriceArray || [],
					// CRM extras
					id: 'rightLeanTo',
					windBracing: data.windBracing,
					jTrim: data.jTrim,
					roofPitchOptions: data.roofPitchOptions,
					rlPrice: parseFloat(rightLeanPrice) || 0,
					rLegPrice: parseFloat(totalRightLegPrice) || 0,
				}),

				// --- CRM-only metadata extras ---
				frontLeanTo: JSON.stringify({
					leanTo: frontLean !== 'none',
					size: {
						width: parseFloat(fLWidth) || 0,
						length: parseFloat(fLLength) || 0,
						height: parseFloat(fLHeight) || 0,
					},
					sidesAndEnds: sideEndData?.configuration?.frontLeanToSide || {},
					items: (() => {
						const sectionItems = doorWindowSelections.filter(item => item.metadata?.section === 'frontLeanTo');
						return sectionItems.length > 0 ? groupItemsBySide(sectionItems) : {};
					})(),
					storage: {
						depth: parseFloat(dephFrontStorage) || 0,
						side: frontLean !== 'none' ? 'front' : null,
					},
					price: prices.frontPriceArray || [],
					id: 'frontLeanTo',
					windBracing: data.windBracing,
					jTrim: data.jTrim,
					roofPitchOptions: data.roofPitchOptions,
					flPrice: parseFloat(frontLeanPrice) || 0,
					fLegPrice: parseFloat(totalFrontLegPrice) || 0,
				}),
				backLeanTo: JSON.stringify({
					leanTo: backLean !== 'none',
					size: {
						width: parseFloat(bLWidth) || 0,
						length: parseFloat(bLLength) || 0,
						height: parseFloat(bLHeight) || 0,
					},
					sidesAndEnds: sideEndData?.configuration?.backLeanToSide || {},
					items: (() => {
						const sectionItems = doorWindowSelections.filter(item => item.metadata?.section === 'backLeanTo');
						return sectionItems.length > 0 ? groupItemsBySide(sectionItems) : {};
					})(),
					storage: {
						depth: parseFloat(dephBackStorage) || 0,
						side: backLean !== 'none' ? 'back' : null,
					},
					price: prices.backPriceArray || [],
					id: 'backLeanTo',
					windBracing: data.windBracing,
					jTrim: data.jTrim,
					roofPitchOptions: data.roofPitchOptions,
					blPrice: parseFloat(backLeanPrice) || 0,
					bLegPrice: parseFloat(totalBackLegPrice) || 0,
				}),
				storage: JSON.stringify({
					depth: (centerStorage === 'none' || !centerStorage) ? 0 : (parseFloat(dephCentralStorage) || 0),
					side: (centerStorage === 'none' || !centerStorage) ? null : centerStorage,
				}),
				centerStorage: JSON.stringify({
					id: 'centerStorage',
					location: storageLocation,
					length: dephCentralStorage,
					csPrice: parseFloat(centerStoragePrice) || 0,
					csLegPrice: parseFloat(totalCenterLegPrice) || 0,
				}),
				leftLeanStorage: JSON.stringify({
					id: 'leftLeanStorage',
					location: storageLocation,
					length: dephLeftStorage,
					llsPrice: parseFloat(leftleanstoragePrice) || 0,
				}),
				rightLeanStorage: JSON.stringify({
					id: 'rightLeanStorage',
					location: storageLocation,
					length: dephRightStorage,
					rlsPrice: parseFloat(rightStoragePrice) || 0,
				}),
				frontLeanStorage: JSON.stringify({
					id: 'frontLeanStorage',
					location: storageLocation,
					length: dephFrontStorage,
					flsPrice: parseFloat(frontStoragePrice) || 0,
				}),
				backLeanStorage: JSON.stringify({
					id: 'backLeanStorage',
					location: storageLocation,
					length: dephBackStorage,
					blsPrice: parseFloat(backStoragePrice) || 0,
				}),
				priceSummary: JSON.stringify({
					subTotal: parseFloat(subTotal) || 0,
					installationFee: installationFee || 0,
					installationFeeAmount: (subTotal * (installationFee || 0)) / 100,
					taxRate: taxRate?.state_rate || 0,
					taxAmount: taxAmount,
					engineeringPlansFee: engineeringPlansFee,
					estimatedTotal: total,
				}),
				sidewallCosts: JSON.stringify(sidewallCosts),
				formAdditionalOptionsData: JSON.stringify({
					...formAdditionalOptionsData,
					panelUpgradePrice,
					anchorsTotalPrice,
					bracingTotalPrice,
					autoBracingTotalPrice,
					autoBracingCount,
					trussesTotalPrice,
					texasCutTotal,
					storagePanelsTotal,
				}),
				centerSectionItems: centerSectionItemsLegacy,
				totalPrice: String(parseFloat(totalEstimate) || 0),
				totalRoofStylePrice: String(parseFloat(totalRoofStylePrice) || 0),
			},
		};

        console.log("Submitting Order Payload:", orderPayload);

		try {
			await dispatch(createOrder(orderPayload)).unwrap();
			await dispatch(fetchClientsOrder(pageSize));
			await dispatch(fetchOrders(pageSize));
			console.log('Order created successfully', orderPayload);
			alert('Order created successfully!');
            if (onClose) onClose();
		} catch (error) {
			console.error('Error creating order:', error);
            const errorMessage = typeof error === 'string' ? error : (error.message || JSON.stringify(error));
            alert(`Error creating order: ${errorMessage}`);
		}
	};

	// ---------------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------------

	return (
		<div className="new-order-page">
			<aside className="new-details-panel">
				{client?.id && (
					<OrderPriceSummary
						subTotal={subTotal}
						installationFee={installationFee}
						taxRate={taxRate}
						loadingTax={loadingTax}
						taxError={taxError}
						onClose={onClose}
						engineeringPlansFee={engineeringPlansFee}
						installationState={clientAddress?.state || ''}
					/>
				)}
				{client?.id ? (
					<OrderClientInfo 
						client={client}
						sections={sections}
						toggleSection={toggleSection}
						handdelDifferentClient={handdelDifferentClient}
						getUserName={getUserName}
					/>
				) : (
					<CreateNewClient onClose={onClose} />
				)}

				{client?.firstName && (
					<form onSubmit={handleSubmit(onSubmit)}>
						{/* Installation addres input */}
						<OrderAddressSection 
							register={register}
							setValue={setValue}
							errors={errors}
							sections={sections}
							toggleSection={toggleSection}
							reset={reset}
						/>
						<OrderInstallerSection 
							register={register}
							installers={installers}
							sections={sections}
							toggleSection={toggleSection}
						/>

						<OrderStyleSection 
							register={register}
							styleOptions={styleOptions}
							selectedStyle={selectedStyle}
							handleOnChange={handleOnChange}
							installationSurfaceOptions={installationSurfaceOptions}
							roofStyleList={roofStyleList}
							selectedRoofStyle={selectedRoofStyle}
							handleRoofStyleChange={handleRoofStyleChange}
							roofPitchOptions={roofPitchOptions}
							selectedRoofPitch={selectedRoofPitch}
							handleRoofPitchChange={handleRoofPitchChange}
							sections={sections}
							toggleSection={toggleSection}
							centerStorage={centerStorage}
							centerStorageOptions={centerStorageOptions}
							handleCenterStorageChange={handleCenterStorageChange}
						/>
						{/* Size */}
						<OrderDimensionsSection 
							toggleSection={toggleSection}
							selectedStyle={selectedStyle}
							sections={sections}
							setCSWidth={setCSWidth}
							cSWidth={cSWidth}
							setCSLength={setCSLength}
							cSLength={cSLength}
							setCSHeight={setCSHeight}
							cSHeight={cSHeight}
							setLLWidth={setLLWidth}
							setLLLength={setLLLength}
							setLLHeight={setLLHeight}
							setRLWidth={setRLWidth}
							setRLLength={setRLLength}
							setRLHeight={setRLHeight}
							setFLWidth={setFLWidth}
							setFLLength={setFLLength}
							setFLHeight={setFLHeight}
							setBLWidth={setBLWidth}
							setBLLength={setBLLength}
							setBLHeight={setBLHeight}
							centerStorage={centerStorage}
							lLWidth={lLWidth}
							lLLength={lLLength}
							lLHeight={lLHeight}
							fLLength={fLLength}
							bLLength={bLLength}
							rLWidth={rLWidth}
							rLLength={rLLength}
							rLHeight={rLHeight}
							bLHeight={bLHeight}
							dephCentralStorage={dephCentralStorage}
							setDephCentralStorage={setDephCentralStorage}
							dephLeftStorage={dephLeftStorage}
							setDephLeftStorage={setDephLeftStorage}
							dephRightStorage={dephRightStorage}
							setDephRightStorage={setDephRightStorage}
							dephFrontStorage={dephFrontStorage}
							setDephFrontStorage={setDephFrontStorage}
							dephBackStorage={dephBackStorage}
							setDephBackStorage={setDephBackStorage}
							leftLean={leftLean}
							rightLean={rightLean}
							backLean={backLean}
							frontLean={frontLean}
						/>

						<OrderSidesEndsSection 
							sections={sections}
							toggleSection={toggleSection}
							onSelectionsChange={setSideWallSidingPanels}
							selectedStyle={selectedStyle}
							formAdditionalOptionsData={formAdditionalOptionsData}
							setLeftLean={setLeftLean}
							setRightLean={setRightLean}
							setFrontLean={setFrontLean}
							setBackLean={setBackLean}
							leftLean={leftLean}
							rightLean={rightLean}
							frontLean={frontLean}
							backLean={backLean}
							gableEndsOptions={gableEndsOptions}
							sidewallOptions={sidewallOptions}
							windBracingOptions={windBracingOptions}
							jTrimOptions={jTrimOptions}
							leanRoofPitch={leanRoofPitch}
							onDataChange={handleDataChange}
							cSWidth={cSWidth}
							cSLength={cSLength}
							cSHeight={cSHeight}
							lLWidth={lLWidth}
							lLLength={lLLength}
							lLHeight={lLHeight}
							bLHeight={bLHeight}
							bLWidth={bLWidth}
							bLLength={bLLength}
							fLLength={fLLength}
							fLWidth={fLWidth}
							fLHeight={fLHeight}
							rLWidth={rLWidth}
							rLLength={rLLength}
							rLHeight={rLHeight}
							control={control}
							sidewallCosts={sidewallCosts}
							setSidewallCosts={setSidewallCosts}
							sideEndData={sideEndData}
						/>

						<OrderAdditionalOptionsSection
							sections={sections}
							toggleSection={toggleSection}
							handleFilterCategoryId={handleFilterCategoryId}
							onDataChangeAdditionalOptions={handleFormDataChange}
							sideWallSidingPanelsData={sideWallSidingPanelsData}
							additionalbracing={additionalbracing}
							AnchorsOptions={AnchorsOptions}
							panelUpgradeOptions={panelUpgradeOptions}
							leftLean={leftLean}
							rightLean={rightLean}
							frontLean={frontLean}
							backLean={backLean}
							centerStorage={centerStorage}
							cSWidth={cSWidth}
							lLWidth={lLWidth}
							rLWidth={rLWidth}
							fLWidth={fLWidth}
							bLWidth={bLWidth}
							sideEndData={sideEndData}
						/>

						<OrderDoorsWindowsSection
							sections={sections}
							toggleSection={toggleSection}
							onSelectionsChange={setDoorWindowSelections}
							centerStorage={centerStorage}
							leftLean={leftLean}
							rightLean={rightLean}
							frontLean={frontLean}
							backLean={backLean}
						/>

						<OrderColorsSection
							sections={sections}
							toggleSection={toggleSection}
							onColorChange={handleColorChange}
							selectedStyle={selectedStyle}
							colors={colors}
						/>

						<OrderWarrantySection 
							sections={sections}
							toggleSection={toggleSection}
							register={register}
						/>

						<OrderDiscountsSection
							sections={sections}
							toggleSection={toggleSection}
							register={register}
						/>

						<OrderEngineeringSection
							sections={sections}
							toggleSection={toggleSection}
							engineeringInput={engineeringInput}
							onEngineeringInputChange={setEngineeringInput}
							engineeringQuote={engineeringQuote}
						/>

						{/* Price sumary */}
						<PricePreview
							sections={sections}
							selectedStyle={selectedStyle}
							toggleSection={toggleSection}
							totalEstimate={totalEstimate}
							discount={watch('discount')}
							cSHeight={cSHeight}
							cSLength={cSLength}
							cSWidth={cSWidth}
							totalCenterLegPrice={totalCenterLegPrice}
							selectedRoofStyle={selectedRoofStyle}
							totalRoofStylePrice={totalRoofStylePrice}
							totalRoofPitchPrice={totalRoofPitchPrice}
							selectedRoofPitch={selectedRoofPitch}
							// Side & End values
							sideWallSidingPanels={sideWallSidingPanels}
							sideWallSidingPanelsData={sideWallSidingPanelsData}
							sideEndData={sideEndData}
							// left lean-to
							lLWidth={lLWidth}
							lLLength={lLLength}
							lLHeight={lLHeight}
							leftLeanPrice={leftLeanPrice}
							totalLeftLegPrice={totalLeftLegPrice}
							leftSideRoofPitchPrice={leftSideRoofPitchPrice}
							// right lean-to
							rLWidth={rLWidth}
							rLLength={rLLength}
							rLHeight={rLHeight}
							rightLeanPrice={rightLeanPrice}
							totalRightLegPrice={totalRightLegPrice}
							rightSideRoofPitchPrice={rightSideRoofPitchPrice}
							// front lean-to
							fLWidth={fLWidth}
							fLLength={fLLength}
							fLHeight={fLHeight}
							frontLeanPrice={frontLeanPrice}
							totalFrontLegPrice={totalFrontLegPrice}
							frontRoofPitchPrice={frontRoofPitchPrice}
							// back lean-to
							bLWidth={bLWidth}
							bLLength={bLLength}
							bLHeight={bLHeight}
							backLeanPrice={backLeanPrice}
							totalBackLegPrice={totalBackLegPrice}
							backRoofPitchPrice={backRoofPitchPrice}
							// Walls values
							sidewallCosts={sidewallCosts}
							installationSurface={watch('installationSurface')}
							doorWindowSelections={doorWindowSelections}
							formAdditionalOptionsData={formAdditionalOptionsData}
							// Prices
							totalCenterGauge={totalCenterGauge}
							insulationPriceBreakdown={prices.insulationPriceBreakdown}
							// Subtotal
							subTotal={subTotal}
							totalDiscount={watch('totalDiscount')}
							taxRate={taxRate}
							// send color values
							colors={colors}
							// wall prices
							onValuesWallsChange={handleValuesWallsChange}
							// Installation fee
							installationFee={installationFee}
							// Storage
							centerStorage={centerStorage}
							dephCentralStorage={dephCentralStorage}
							centerStoragePrice={centerStoragePrice}
							leftLean={leftLean}
							dephLeftStorage={dephLeftStorage}
							leftleanstoragePrice={leftleanstoragePrice}
							rightLean={rightLean}
							dephRightStorage={dephRightStorage}
							rightStoragePrice={rightStoragePrice}
							frontLean={frontLean}
							dephFrontStorage={dephFrontStorage}
							frontStoragePrice={frontStoragePrice}
							backLean={backLean}
							dephBackStorage={dephBackStorage}
							backStoragePrice={backStoragePrice}
							storagePanelsTotal={storagePanelsTotal}
							storagePanelsBreakdown={storagePanelsBreakdown}
							panelUpgradePrice={panelUpgradePrice}
							anchorsTotalPrice={anchorsTotalPrice}
							bracingTotalPrice={bracingTotalPrice}
							autoBracingTotalPrice={autoBracingTotalPrice}
							autoBracingCount={autoBracingCount}
							trussesTotalPrice={trussesTotalPrice}
							texasCutTotal={texasCutTotal}
							foamClosureTotal={foamClosureTotal}
							engineeringPlansFee={engineeringPlansFee}
						/>
						<StyleSettings
							selectedStyle={selectedStyle}
							setCSWidth={setCSWidth}
							setCSLength={setCSLength}
							setCSHeight={setCSHeight}
							setLLWidth={setLLWidth}
							setLLLength={setLLLength}
							setLLHeight={setLLHeight}
							setRLWidth={setRLWidth}
							setRLLength={setRLLength}
							setRLHeight={setRLHeight}
							setFLWidth={setFLWidth}
							setFLLength={setFLLength}
							setFLHeight={setFLHeight}
							setBLWidth={setBLWidth}
							setBLLength={setBLLength}
							setBLHeight={setBLHeight}
							setLeftLean={setLeftLean}
							setRightLean={setRightLean}
							setCenterStorage={setCenterStorage}
							setDephCentralStorage={setDephCentralStorage}
						/>

						{(() => { const s = (clientAddress?.state || '').trim().toLowerCase(); return s && s !== 'ca' && s !== 'california'; })() && (
							<div className="mx-4 mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
								<span className="text-amber-500 text-base mt-0.5">⚠</span>
								<p className="text-amber-700 text-xs font-medium leading-snug">
									Please double check the prices outside of California before submitting the quote.
								</p>
							</div>
						)}
						<div className="btns-footer">
							<button type="submit" className="redBtn mr-4">
								Submit Order
							</button>
						</div>
					</form>
				)}
			</aside>
			
			<Modal
				isOpen={showResetModal}
				onRequestClose={cancelReset}
				style={customStyles}
				contentLabel="Confirm Style Change"
			>
				<div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl mx-4">
					<div className="mb-4">
						<h3 className="text-xl font-bold text-gray-900">Change Building Style?</h3>
						<p className="mt-2 text-gray-600">
							Changing the building style will reset your current configuration and selections to their defaults. Are you sure you want to continue?
						</p>
					</div>
					<div className="flex justify-end gap-3 mt-6">
						<button
							onClick={cancelReset}
							className="px-4 py-2 rounded-lg text-gray-600 font-medium hover:bg-gray-100 transition-colors"
						>
							Cancel
						</button>
						<button
							onClick={confirmReset}
							className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-200"
						>
							Yes, Start Over
						</button>
					</div>
				</div>
			</Modal>
		</div>
	);

}
